//@flow
import React, { Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import { HeaderRoot } from './HeaderRoot';
import { OverviewRoot } from './OverviewRoot';
import { ProjectsRoot } from './ProjectsRoot';
import { FavoritesRoot } from './FavoritesRoot';

import { ProfileRootQuery } from './ProfileRootQuery';

import { logError } from '../helpers';
import { trackPageView } from '../shared/helpers/tracking';

import { PAGE_TYPE_OVERVIEW, PAGE_TYPE_PROJECTS, PAGE_TYPE_FAVORITES } from './constants';
import type { DesignProfileWrapper_viewer as Viewer } from './__generated__/DesignProfileWrapper_viewer.graphql';
import type { RelayRefetchProp } from 'react-relay';

import type { PageType } from './constants';

import styles from './DesignProfileWrapper.scss';

type Props = {|
    adminHost: string,
    firmUrlLabel: string,
    initialPage: PageType,
    isEditMode: boolean,
    isAdminMode: boolean,
    isTrade: boolean,
    isClient?: boolean,
    viewer?: ?Viewer,
    relay?: RelayRefetchProp,
    isMobile: boolean,
|};

type State = {
    currentPage: PageType,
    shouldRenderOverview: boolean,
    shouldRenderProjects: boolean,
};

export class DesignProfileWrapperComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            currentPage: props.initialPage,
            shouldRenderOverview: props.initialPage === PAGE_TYPE_OVERVIEW,
            shouldRenderProjects: props.initialPage === PAGE_TYPE_PROJECTS,
        };
    }

    componentDidMount() {
        // After mounting, refetch to get all of the data immediately vs. on-demand for faster tab nav
        const outputVars = {
            firmUrlLabel: this.props.firmUrlLabel,
            isEditMode: this.props.isEditMode,
            shouldRenderOverview: true,
            shouldRenderProjects: true,
        };

        window.onpopstate = this.handlePopState;

        if (this.props.relay) {
            this.props.relay.refetch(outputVars, undefined, (e: ?Error) => {
                if (e) {
                    logError(e);
                }

                this.setState({
                    shouldRenderOverview: true,
                    shouldRenderProjects: true,
                });
            });
        }

        trackPageView();
    }

    componentWillUnmount() {
        window.onpopstate = null;
    }

    onChangePage = (pageType: PageType) => {
        if (this.state.currentPage !== pageType) {
            this.setState({ currentPage: pageType });

            // edit mode should keep the same url, regardless of active tab, and should not track vpvs
            if (!this.props.isEditMode) {
                window.history.pushState(
                    {},
                    null,
                    `/design-firms/${this.props.firmUrlLabel}/${
                        pageType !== PAGE_TYPE_OVERVIEW ? `${pageType}/` : ''
                    }`
                );
                trackPageView({ isVpv: true });
            }
        }
    };

    getCurrentPageType = (): PageType => {
        const path = location.pathname;

        // regex used here is for getting last segment of pathname
        // it gets string value before or after (if pathname does not have ending "/") last "/"
        const pageType = (path.match(/([^\/]*)\/*$/) || [])[1];

        switch (pageType) {
            case PAGE_TYPE_PROJECTS:
                return PAGE_TYPE_PROJECTS;
            case PAGE_TYPE_FAVORITES:
                return PAGE_TYPE_FAVORITES;
            default:
                return PAGE_TYPE_OVERVIEW;
        }
    };

    handlePopState = () => {
        const pageType = this.getCurrentPageType();
        this.setState({ currentPage: pageType });
    };

    render() {
        let pageElement = null; // TODO error display

        switch (this.state.currentPage) {
            case PAGE_TYPE_OVERVIEW: {
                pageElement = (
                    <OverviewRoot
                        adminHost={this.props.adminHost}
                        viewer={(this.props.viewer: ?Object)}
                        isClient={this.props.isClient || false}
                        isAdminMode={this.props.isAdminMode}
                        isEditMode={this.props.isEditMode}
                        onChangePage={this.onChangePage}
                        isTrade={this.props.isTrade}
                    />
                );
                break;
            }
            case PAGE_TYPE_PROJECTS: {
                pageElement = (
                    <ProjectsRoot
                        adminHost={this.props.adminHost}
                        viewer={(this.props.viewer: ?Object)}
                        isClient={this.props.isClient || false}
                        isAdminMode={this.props.isAdminMode}
                        isEditMode={this.props.isEditMode}
                    />
                );
                break;
            }
            case PAGE_TYPE_FAVORITES: {
                pageElement = (
                    <FavoritesRoot
                        viewer={(this.props.viewer: ?Object)}
                        isEditMode={this.props.isEditMode}
                        firmUrlLabel={this.props.firmUrlLabel}
                        isMobile={this.props.isMobile}
                    />
                );
                break;
            }
        }

        return (
            <div>
                <HeaderRoot
                    viewer={(this.props.viewer: ?Object)}
                    currentPage={this.state.currentPage}
                    isEditMode={this.props.isEditMode}
                    onChangePage={this.onChangePage}
                />
                <div className={styles.content}>{pageElement}</div>
            </div>
        );
    }
}

export const DesignProfileWrapper = createRefetchContainer(
    DesignProfileWrapperComponent,
    {
        viewer: graphql`
            fragment DesignProfileWrapper_viewer on Viewer {
                ...HeaderRoot_viewer
                ...OverviewRoot_viewer
                ...ProjectsRoot_viewer
            }
        `,
    },
    ProfileRootQuery
);
